import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * When maintenance mode is enabled, block the UI for everybody except admin.
 * Admin routes (/admin/*) and auth pages remain accessible so admin can toggle it off.
 */
export default function MaintenanceGate({ children }) {
  const { role, user } = useAuth();
  const loc = useLocation();
  const [state, setState] = useState(null);

  const isAdmin = role === "client" && user?.isAdmin;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/system/maintenance`);
        if (!cancelled) setState(data);
      } catch {
        if (!cancelled) setState({ enabled: false });
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!state) return children; // don't block initial render
  if (!state.enabled) return children;
  if (isAdmin) return children;

  // Spec utilisateur Feb 2026 — en maintenance, la page dédiée
  // « /admin/login » (login exclusivement admin) est le seul chemin
  // d'auth ouvert. On garde aussi `/forgot-password` accessible pour
  // qu'un admin ayant oublié son mot de passe puisse le réinitialiser.
  const authPaths = ["/admin/login", "/forgot-password"];
  const onAuth = authPaths.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  if (onAuth) return children;

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4" data-testid="maintenance-overlay">
      <div className="max-w-lg text-center bg-white border border-border p-10">
        <div className="h-14 w-14 mx-auto bg-[#FFFBE6] flex items-center justify-center">
          <AlertTriangle size={28} className="text-[#9A7D00]" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-black text-ink">Site en cours de maintenance</h1>
        <p className="mt-4 text-subtle leading-relaxed whitespace-pre-wrap" data-testid="maintenance-message">
          {state.message || "Nous revenons très vite."}
        </p>
        <p className="mt-8 text-xs text-subtle">
          Si vous êtes administrateur, <a href="/admin/login" className="text-brand underline" data-testid="maintenance-admin-login-link">connectez-vous</a> pour accéder à la console.
        </p>
      </div>
    </div>
  );
}
